import {
  GetString,
} from 'fluent-react';
import zip from 'lodash-es/zip';
import React, { RefObject } from 'react';
import styled from 'styled-components';
import {
  SectionNavContainer,
} from '../Grid';
import HelpTooltip from '../utilityBarItems/HelpTooltip';
import {
  primaryColor1,
} from '../Utils';
import Circle from './Circle';
import usePromotableZIndex from './usePromotableZIndexStaticListOfElements';
import {
  determineProgressForEachIcon,
  ISectionInfo,
  staticCircleOuterRadius,
  zIndices as localZIndices,
} from './Utils';

const getSectionInfo = (
    getFluentString: GetString,
    args: {year: number, countryName: string},
  ): ISectionInfo[] => [
  {
    svgFileName: 'nav-icon-1.svg',
    title: getFluentString('section-nav-section-economic-structure'),
    slideLabels: [
      getFluentString('section-nav-slide-country-pages-home'),
      getFluentString('section-nav-slide-export-basket', {year: args.year.toString()}),
      getFluentString('section-nav-slide-export-complexity', {year: args.year.toString()}),
    ],
  },
  {
    svgFileName: 'nav-icon-2.svg',
    title: getFluentString('section-nav-section-market-dynamics'),
    slideLabels: [
      getFluentString('section-nav-slide-export-growth-dynamics'),
      getFluentString('section-nav-slide-market-share'),
      getFluentString('section-nav-slide-diversification-new-product'),
    ],
  },
  {
    svgFileName: 'nav-icon-3.svg',
    title: getFluentString('section-nav-section-strategy-space'),
    slideLabels: [
      getFluentString('section-nav-slide-diversification-paths'),
      getFluentString('section-nav-slide-diversification-countries-paths',
        {'country-name': args.countryName},
      ),
      getFluentString(
        'section-nav-slide-diversification-strategic-approach'),
    ],
  },
  {
    svgFileName: 'nav-icon-4.svg',
    title: getFluentString('section-nav-section-diversification-path'),
    slideLabels: [
      getFluentString('section-nav-slide-growth-opportunities'),
      getFluentString('section-nav-slide-high-potential-products'),
      getFluentString('section-nav-slide-growth-expectations',
        {'country-name': args.countryName},
      ),
    ],
  },
];

//#region Styling
const numIcons = getSectionInfo(() => '', {year: 0, countryName: ''}).length;
const gapBetweenIcons = '1.25rem';
const gridTemplateColumnsStyle =
  // tslint:disable-next-line:max-line-length
  `1fr repeat(${numIcons - 1}, ${staticCircleOuterRadius * 2}px ${gapBetweenIcons}) ${staticCircleOuterRadius * 2}px 1fr`;

const Root = styled(SectionNavContainer)`
  justify-content: center;
  align-items: center;
  display: grid;
  grid-template-columns: ${gridTemplateColumnsStyle};
  border-style: dotted;
  border-color: ${primaryColor1};
  border-width: 1px 0;
`;
const CircleContainer = styled.div`
  grid-row: 1;
`;

//#endregion

export const getSlideIndiciesBySection = (startingIndex: number, info: ISectionInfo[]) => {
  const result: number[][] = [];
  let counter = startingIndex;
  for (const {slideLabels} of info) {
    const numSlides = slideLabels.length;
    const indicesForSection: number[] = [];
    for (let i = 0; i < numSlides; i += 1) {
      indicesForSection.push(counter);
      counter += 1;
    }
    result.push(indicesForSection);
  }
  return result;
};

export interface IProps {
  // The index of the first slide of the first section, which can be greater
  // than 0 if the there is a landing page:
  startingIndexOfFirstSection: number;
  slideIndex: number;
  setSlideIndex: (index: number) => void;
  year: number;
  countryName: string;
  getFluentString: GetString;
  zIndex: number;
  rootElRef: RefObject<HTMLDivElement>;
  isHelpActive: boolean;
  helpTooltipIndex: number;
}

const SectionNav = (props: IProps) => {
  const {
    slideIndex, setSlideIndex,
    startingIndexOfFirstSection,
    getFluentString, year, countryName,
    zIndex: zIndexFromAbove,
    rootElRef, helpTooltipIndex, isHelpActive,
  } = props;
  const sectionInfo = getSectionInfo(getFluentString, {year, countryName});
  const relativeSlideIndex = slideIndex - startingIndexOfFirstSection;
  const progresses = determineProgressForEachIcon(relativeSlideIndex, sectionInfo);
  const slideIndicesBySection = getSlideIndiciesBySection(startingIndexOfFirstSection, sectionInfo);
  const outerZipped = zip(progresses, slideIndicesBySection, sectionInfo) as Array<[number, number[], ISectionInfo]>;

  const {
    getZIndex, promoteZIndex, restoreZIndex,
  } = usePromotableZIndex({
    numElements: outerZipped.length,
    promotedZIndex: localZIndices.promoted,
  });

  const content = outerZipped.map(([progress, slideIndices, {svgFileName, slideLabels, title}], index) => {
    const gridColumn = (index + 1) * 2;
    const innerZipped = zip(slideIndices, slideLabels) as Array<[number, string]>;
    const tooltpItems = innerZipped.map(([innerIndex, label]) => ({slideIndex: innerIndex, label}));
    return (
      <React.Fragment key={`icon-${index}`}>
        <CircleContainer style={{gridColumn}}>
          <Circle
            svgFileName={svgFileName}
            title={title}
            progress={progress}
            tooltipItems={tooltpItems}
            onTooltipItemClick={setSlideIndex}
            zIndex={getZIndex(index)}
            promoteZIndex={() => promoteZIndex(index)}
            restoreDefaultZIndex={() => restoreZIndex(index)}
          />
        </CircleContainer>
      </React.Fragment>
    );
  });
  const style: React.CSSProperties = {
    zIndex: zIndexFromAbove,
  };

  return (
    <Root style={style} ref={rootElRef}>
      {content}
      <HelpTooltip
        index={helpTooltipIndex}
        term={getFluentString('help-tooltips-circle-nav-title')}
        explanation={getFluentString('help-tooltips-circle-nav-description')}
        isHelpActive={isHelpActive}
        styles={{}}
      />
    </Root>
  );
};

export default SectionNav;
